import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
} from 'lucide-react';

interface EditChunkModalProps {
  segment?: any | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (segment: any) => void;
}

const EditChunkModal: React.FC<EditChunkModalProps> = ({ segment, isOpen, onClose, onSave }) => {
  const [editedContent, setEditedContent] = useState('');
  const [editedKeywords, setEditedKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');

  useEffect(() => {
    if (segment) {
      setEditedContent(segment?.content);
      setEditedKeywords([...segment?.keywords]);
    } else {
      setEditedContent('');
      setEditedKeywords([]);
    }
  }, [segment]);

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !editedKeywords.includes(newKeyword.trim())) {
      setEditedKeywords([...editedKeywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setEditedKeywords(editedKeywords.filter(keyword => keyword !== keywordToRemove));
  };

  const handleSave = () => {
    if (segment) {
      const updatedSegment = {
        ...segment,
        content: editedContent,
        keywords: editedKeywords,
        description: editedContent
      };
      onSave(updatedSegment);
    }
  };

  const handleKeywordInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddKeyword();
    }
  };

  if (!isOpen || !segment) return null;

  return (
    <div className="mt-5 lg:mt-0 fixed inset-0 flex items-center justify-center lg:justify-end z-50 p-4 lg:p-0">
      <div className="bg-black bg-opacity-50 sm:bg-opacity-0 absolute inset-0"></div>
      <div className="bg-white w-full max-w-md lg:w-96 h-full max-h-full lg:max-h-none shadow-xl flex flex-col relative rounded-lg lg:rounded-none">
        {/* Header */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{segment?.id ? 'Edit Chunk' : 'Add Chunk'}</h2>
            <button
              onClick={(e) => { onClose() }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
            <span className="text-sm font-medium text-gray-700">
              Chunk-{String(segment?.position).padStart(2, '0')}
            </span>
            <span className="text-sm text-gray-500">
              {segment?.wordCount ?? 0} characters
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-auto">
          <div className="space-y-4 sm:space-y-6">
            {/* Content Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-48 sm:h-64 p-3 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter chunk content..."
              />
            </div>

            {/* Keywords Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords
              </label>

              {/* Add Keyword Input */}
              <div className="flex flex-row sm:space-y-0 sm:space-x-2 mb-3 gap-2">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyPress={handleKeywordInputKeyPress}
                  className="flex-1 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add keyword"
                />
                <button
                  onClick={handleAddKeyword}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center justify-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span className="">Add</span>
                </button>
              </div>

              {/* Keywords List */}
              <div className="flex flex-wrap gap-1">
                {editedKeywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium"
                  >
                    #{keyword}
                    <button
                      onClick={() => handleRemoveKeyword(keyword)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {editedKeywords.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4 w-full">
                    No keywords added yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex flex-row gap-1">
            <button
              onClick={(e) => { onClose() }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 order-2 sm:order-1"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center space-x-2 order-1 sm:order-2"
            >
              {/* <Save className="w-4 h-4" /> */}
              <span>Save</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditChunkModal;
